// src/ui/AppShell.jsx
// The frame around every signed-in page: one sidebar with every tool, the loaded
// workbook and sign-out, collapsible to an icon rail (remembered per browser)
// and a drawer on phones. Pages render inside <Outlet />.

import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  Activity,
  ChartNoAxesCombined,
  FileSpreadsheet,
  FlaskConical,
  LayoutDashboard,
  Layers,
  LogOut,
  Menu,
  PanelLeftClose,
  PanelLeftOpen,
  PenTool,
  Radar,
  Swords,
  TableProperties,
  Upload,
} from "lucide-react";
import { ymd } from "./format";

export const AUTH_STORAGE_KEY = "fsrov_auth";
const COLLAPSE_KEY = "fsrov_sidebar_collapsed";

export const NAV = [
  {
    label: "วิเคราะห์ทีม",
    items: [
      { to: "/dashboard", label: "ภาพรวมทีม", desc: "ผลแข่ง · คู่แข่ง · ฮีโร่", icon: LayoutDashboard },
      { to: "/charts", label: "ห้องกราฟ", desc: "กราฟหลายมุม 20 แบบ", icon: ChartNoAxesCombined },
      { to: "/team-performance", label: "Team Performance", desc: "ซ้อม vs แข่ง รายฮีโร่", icon: Activity },
      { to: "/draft-recap", label: "Draft Recap", desc: "แบน/พิกทุกเกม", icon: TableProperties },
      { to: "/tier-data", label: "Tier Data", desc: "ฮีโร่และคอมโบ", icon: Layers },
    ],
  },
  {
    label: "เครื่องมือ",
    items: [
      { to: "/draft", label: "Draft Simulator", desc: "จำลองแบน/พิก BO", icon: Swords },
      { to: "/tactic-board", label: "Sucrose Tactic", desc: "กระดานวางแผน", icon: PenTool },
      { to: "/minimap-lab", label: "Minimap Lab", desc: "แกงก์ · เส้นทาง · ไฟต์", icon: Radar },
      { to: "/meta-lab", label: "Meta Lab", desc: "ฮีโร่ · ไอเทม · ดาเมจ", icon: FlaskConical },
    ],
  },
  {
    label: "ข้อมูล",
    items: [{ to: "/upload", label: "ไฟล์ข้อมูล", desc: "อัปโหลด / เปลี่ยนไฟล์", icon: Upload }],
  },
];

const ALL_ITEMS = NAV.flatMap((g) => g.items);

function readCollapsed() {
  try {
    return localStorage.getItem(COLLAPSE_KEY) === "1";
  } catch {
    return false;
  }
}

/**
 * workbook: { name, games, latest, sheets } | null — what the sidebar shows about the loaded file.
 */
export default function AppShell({ workbook }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [drawer, setDrawer] = useState(false);

  useEffect(() => setDrawer(false), [location.pathname]);

  const toggleCollapsed = () => {
    setCollapsed((c) => {
      try {
        localStorage.setItem(COLLAPSE_KEY, c ? "0" : "1");
      } catch {
        /* private mode */
      }
      return !c;
    });
  };

  const signOut = () => {
    try {
      localStorage.removeItem(AUTH_STORAGE_KEY);
    } catch {
      /* ignore */
    }
    navigate("/", { replace: true });
  };

  const current = ALL_ITEMS.find((it) => location.pathname.startsWith(it.to));
  const hasFile = Boolean(workbook?.name);
  const fileName = hasFile ? String(workbook.name).replace(/\.(xlsx|xls)$/i, "") : "";

  return (
    <div className="ds-app" data-collapsed={collapsed ? "true" : "false"} data-drawer={drawer ? "open" : "closed"}>
      <aside className="ds-sidebar" aria-label="เมนูหลัก">
        <div className="ds-brand">
          <img
            src={`${process.env.PUBLIC_URL}/full-sense-logo.jpg`}
            alt=""
            onError={(e) => {
              e.currentTarget.src = `${process.env.PUBLIC_URL}/full-sense-favicon.png`;
            }}
          />
          <div className="ds-brand-text">
            <div className="ds-brand-title">FULL SENSE</div>
            <div className="ds-brand-sub">ROV Analytics</div>
          </div>
        </div>

        <nav className="ds-nav">
          {NAV.map((group) => (
            <div className="ds-nav-group" key={group.label}>
              <div className="ds-nav-label">{group.label}</div>
              {group.items.map((it) => {
                const Icon = it.icon;
                return (
                  <NavLink key={it.to} to={it.to} className="ds-nav-item" title={collapsed ? `${it.label} — ${it.desc}` : it.desc}>
                    <Icon size={18} strokeWidth={2} />
                    <span className="ds-nav-text">
                      {it.label}
                      <span className="ds-nav-desc">{it.desc}</span>
                    </span>
                  </NavLink>
                );
              })}
            </div>
          ))}
        </nav>

        <div className="ds-sidebar-foot">
          <div className="ds-wb" title={hasFile ? workbook.name : "ยังไม่ได้อัปโหลดไฟล์"}>
            <div className="ds-wb-icon" data-empty={hasFile ? "false" : "true"}>
              <FileSpreadsheet size={16} strokeWidth={2.2} />
            </div>
            <div className="ds-wb-body">
              {hasFile ? (
                <>
                  <div className="ds-wb-name">{fileName}</div>
                  <div className="ds-wb-meta">
                    {workbook.games ? `${workbook.games.toLocaleString("en-US")} เกม` : "กำลังอ่าน…"}
                    {workbook.latest ? ` · ล่าสุด ${ymd(workbook.latest)}` : ""}
                  </div>
                </>
              ) : (
                <>
                  <div className="ds-wb-name">ยังไม่มีไฟล์ข้อมูล</div>
                  <div className="ds-wb-meta">หน้าสถิติต้องใช้ไฟล์ .xlsx</div>
                </>
              )}
              <button type="button" className="ds-wb-link" onClick={() => navigate("/upload")}>
                <Upload size={12} strokeWidth={2.4} />
                {hasFile ? "เปลี่ยนไฟล์" : "อัปโหลดไฟล์"}
              </button>
            </div>
          </div>
          <div className="ds-foot-row">
            <button type="button" className="ds-icon-btn ds-collapse-btn" onClick={toggleCollapsed} title={collapsed ? "ขยายเมนู" : "ย่อเมนู"} aria-label={collapsed ? "ขยายเมนู" : "ย่อเมนู"}>
              {collapsed ? <PanelLeftOpen size={16} /> : <PanelLeftClose size={16} />}
            </button>
            <button type="button" className="ds-icon-btn" onClick={signOut} title="ออกจากระบบ" aria-label="ออกจากระบบ">
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </aside>

      <div className="ds-scrim" onClick={() => setDrawer(false)} />

      <main className="ds-main">
        <div className="ds-mobilebar">
          <button type="button" className="ds-icon-btn" onClick={() => setDrawer(true)} aria-label="เปิดเมนู">
            <Menu size={18} />
          </button>
          <div className="ds-mobilebar-title">{current?.label || "FS ROV Analytics"}</div>
        </div>
        <Outlet />
      </main>
    </div>
  );
}
